import { FunctionComponent, memo, useEffect, useMemo, useState } from "react";

interface IProps {
	width?: string;
	height?: string;
}

const absCoordsFromProgressNoOffset = (progess: number, radius: number): { x: number, y: number } => {
	const radians = -2 * Math.PI * progess;
	const relX = Math.sin(radians) * radius;
	const relY = Math.cos(radians) * radius;
	return { x: relX, y: relY };
}

const absCoordsFromProgress = (progess: number, radius: number): { x: number, y: number } => {
	const { x: relX, y: relY } = absCoordsFromProgressNoOffset(progess, radius);
	const xOffset = 50;
	const yOffset = 50;
	return { x: xOffset - relX, y: yOffset - relY };
}

const progressArcD = (progress0: number, progressF: number, radius: number) => {
	if (progress0 === progressF) { return ""; }
	const start = absCoordsFromProgress(progress0, radius);
	const end = absCoordsFromProgress(progressF, radius);
	const useBigArc = ((progressF - progress0) > 0.5) ? 1 : 0;
	// M x0 y0 A rX rY 0 <make big arc> <clockwise> xF yF
	return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${useBigArc} 1 ${end.x} ${end.y}`;
}

// M10 50 A40 40 0 0 0 90 50 A40 42 0 0 1 10 50
const sliverArcD = (progress0: number, progressF: number, radius: number): string => {
	if (progress0 === progressF) { return ""; }
	if (progressF >= 1) { return sliverArcD(progress0, progressF - 1, radius); }
	// if (progress0 > progressF) { return sliverArcD(progressF, progress0, radius); }
	const start = absCoordsFromProgress(progress0, radius);
	const end = absCoordsFromProgress(progressF, radius);
	const useBigArc = ((progressF - progress0) > 0.5) ? 1 : 0;

	const midPoint = (progress0 + progressF) / 2;
	// M x0 y0 A rX rY 0 <make big arc> <clockwise> xF yF
	return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${useBigArc} 1 ${end.x} ${end.y} A ${radius} ${radius-4} ${midPoint * 360} ${useBigArc} 0 ${start.x} ${start.y}`;
}

const speed = 1;
const animationLife = 1000 * speed;
const spf = 50;
const blastEnd = 200 * speed;
const arcEnd = 900 * speed;

const triangleMeta = () => {
	return { c: Math.random(), d: Math.random() };
}

const InOutQuadBlend = (t: number) =>
{
	if(t <= 0.5)
		return 2 * t * t;
	t -= 0.5;
	return 2.0 * t * (1.0 - t) + 0.5;
}

const ClickArcs: FunctionComponent<IProps> = (props) => {

	const [a0] = useState(Math.random());
	const [a1] = useState(Math.random());
	const [triangleCenters] = useState([triangleMeta(), triangleMeta(), triangleMeta(), triangleMeta()]);
	const [t, setT] = useState(0);

	useEffect(() => {
		const move = () => {
			setT(t => t >= animationLife ? animationLife : t + spf);
		}
		const timeout = setTimeout(move, spf);
		return () => {
			clearTimeout(timeout);
		}
	}, [t]);

	let arcLength0 = t > arcEnd ? 0 : (.5 - InOutQuadBlend(.5 * ((t) / arcEnd)));
	let arcLength = arcLength0 > .5 ? .5 : arcLength0;
	let blastOpacity = t > blastEnd ? 0 : .2;
	let diameter = ((t / animationLife) * 100) + "%";
	let triangleSize = t > animationLife ? 0 : (t > arcEnd) ? 4 * (1 - ((t - arcEnd)/(animationLife - arcEnd))) : 4;
	// arcLength = .5;
	// blastOpacity = .2;
	// diameter = "100%";
	// triangleSize = 4;

	const triangles = triangleCenters.map(({ c, d }) => {
		const center = absCoordsFromProgress(c, 40);
		const p0 = absCoordsFromProgressNoOffset(d + 0, triangleSize);
		const p1 = absCoordsFromProgressNoOffset(d + .333, triangleSize);
		const p2 = absCoordsFromProgressNoOffset(d + .667, triangleSize);
		return <polygon key={c} points={`${center.x + p0.x},${center.y + p0.y} ${center.x + p1.x},${center.y + p1.y} ${center.x + p2.x},${center.y + p2.y}`} 
			style={{ fill: "var(--colorful-loading-blue)" }}
		/>;
	})

	return <svg version="1.1" id="L7" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
		viewBox="0 0 104 104" enableBackground="new 0 0 100 100" xmlSpace="preserve" width={diameter} height={diameter}>
		<g fillOpacity={0.9}>
			<path style={{ fill: "var(--colorful-loading-blue)" }} strokeLinecap="round" d={sliverArcD(a0, a0 + arcLength, 40)} />
			<path style={{ fill: "var(--colorful-loading-blue)" }} strokeLinecap="round" d={sliverArcD(a1, a1 + arcLength, 36)} />
			<path style={{ fill: "var(--colorful-loading-blue)" }} strokeLinecap="round" d={progressArcD(0, .999, 40)} fillOpacity={blastOpacity}  /> 
			{triangles}
		</g>		
	</svg>;
}

export default memo(ClickArcs);